import { assign, createMachine } from "xstate";
import { addAxiosErrorToContextAction } from "./actions/addAxiosErrorToContextAction";
import { addBookingValuesAction } from "./actions/addBookingValuesAction";
import { addValidationErrorAction } from "./actions/addValidationErrorAction";
import { clearValidationErrorAction } from "./actions/clearValidationErrorAction";
import { setBookingTypeAction } from "./actions/setBookingTypeAction";
import { setSelectedCurrencyAction } from "./actions/setSelectedCurrencyAction";
import { setSymbolAction } from "./actions/setSymbolAction";
import { persistBooking } from "./services/persistBooking";
import { validateBooking } from "./services/validateBooking";
import { TransactionWizardState } from "./types/transactionWizardState";
import {
  defaultContext,
  TransactionWizardContext,
} from "./types/transactionWizardContext";
import { TransactionWizardEvent } from "./types/TransactionWizardEvent";
import { loadInvestment } from "./services/loadInvestment";
import { setInvestmentValuesAction } from "./actions/setInvestmentValuesAction";
import { setSourceInvestmentIdAction } from "./actions/setSourceInvestmentIdAction";
import { loadBookings } from "./services/loadBookings";
import { setBookingsAction } from "./actions/setBookingsAction";
import { updateSplitValuesAction } from "./actions/updateSplitValuesAction";
import { persistSplit } from "./services/persistSplit";
import { setExchangeRateAction } from "./actions/setExchangeRateAction";

export const addTransactionWizardSM = createMachine<
  TransactionWizardContext,
  TransactionWizardEvent,
  TransactionWizardState
>(
  {
    id: "addTransactionWizardSM",

    // Initial state
    initial: "idle",

    // Local context for entire machine
    context: defaultContext,

    // State definitions
    states: {
      idle: {
        on: {
          INIT_FROM_INVESTMENT: { target: "loadInvestment" },
        },
      },
      loadInvestment: {
        id: "loadInvestment",
        invoke: {
          id: "loadInvestment",
          src: loadInvestment,
        },
        on: {
          INVESTMENT_INITIALIZED: {
            target: "selectBookingType",
            actions: ["setInvestmentValuesAction"],
          },
          SOMETHING_WRONG: { target: "somethingWrong" },
        },
      },
      selectBookingType: {
        id: "selectBookingType",
        on: {
          BOOKING_TYPE_SELECTED: {
            target: "priceAndDate",
            actions: ["setBookingTypeAction"],
          },
          CAPITAL_MEASURE_SELECTED: {
            target: "searchSymbol",
            actions: ["setBookingTypeAction", "setSourceInvestmentIdAction"],
          },
          SPLIT_SELECTED: {
            target: "split",
          },
        },
      },
      searchSymbol: {
        on: {
          INIT_FROM_SYMBOL: { target: "priceAndDate" },
          BACK: { target: "idle" },
        },
        exit: ["setSymbolAction"],
      },
      priceAndDate: {
        id: "priceAndDate",
        on: {
          VALIDATE_BOOKING: {
            target: "validateBooking",
            actions: ["addBookingValuesAction", "setSelectedCurrencyAction"],
          },
          SOMETHING_WRONG: { target: "somethingWrong" },
          BACK: [
            {
              target: "searchSymbol",
              cond: (ctx) =>
                ctx.bookingValues.type === "switch" ||
                ctx.bookingValues.type === "carve_out",
            },
            { target: "idle" },
          ],
        },
      },
      validateBooking: {
        invoke: {
          id: "validateBooking",
          src: validateBooking,
        },
        on: {
          BOOKING_VALID: [
            {
              target: "persist",
              actions: ["clearValidationErrorAction"],
              cond: (context) => Boolean(context.skipSummaryStep),
            },
            {
              target: "summary",
              actions: ["clearValidationErrorAction"],
            },
          ],
          BOOKING_INVALID: {
            target: "priceAndDate",
            actions: ["addValidationErrorAction"],
          },
          SOMETHING_WRONG: { target: "somethingWrong" },
        },
      },
      summary: {
        on: {
          PERSIST: { target: "persist" },
          BACK: { target: "priceAndDate" },
        },
      },
      persist: {
        invoke: {
          id: "persistBooking",
          src: persistBooking,
        },
        on: {
          BOOKING_VALID: {
            target: "persisted",
          },
          BOOKING_INVALID: {
            target: "priceAndDate",
            actions: ["addValidationErrorAction"],
          },
          SOMETHING_WRONG: { target: "somethingWrong" },
        },
      },
      split: {
        initial: "loadBookings",
        states: {
          loadBookings: {
            invoke: { id: "loadBookings", src: loadBookings },
            on: {
              BOOKINGS_LOADED: {
                target: "ratioAndDate",
                actions: ["setBookingsAction"],
              },
              SOMETHING_WRONG: { target: "#somethingWrong" },
            },
          },
          ratioAndDate: {
            on: {
              PREVIEW: {
                target: "preview",
                actions: ["updateSplitValuesAction"],
              },
              BACK: { target: "#selectBookingType" },
            },
          },
          preview: {
            on: {
              PERSIST: { target: "persist" },
              BACK: { target: "ratioAndDate" },
            },
          },
          persist: {
            invoke: {
              id: "persistSplit",
              src: persistSplit,
            },
            on: {
              BOOKING_VALID: {
                target: "#persisted",
              },
              BOOKING_INVALID: {
                target: "ratioAndDate",
                actions: ["addValidationErrorAction"],
              },
              SOMETHING_WRONG: { target: "#somethingWrong" },
            },
          },
        },
      },
      persisted: {
        type: "final",
        id: "persisted",
      },
      somethingWrong: {
        type: "final",
        id: "somethingWrong",
        entry: ["addAxiosErrorToContextAction"],
      },
    },
  },
  {
    actions: {
      setSymbolAction: assign(setSymbolAction),
      setSelectedCurrencyAction: assign(setSelectedCurrencyAction),
      setBookingTypeAction: assign(setBookingTypeAction),
      setExchangeRateAction: assign(setExchangeRateAction),
      setInvestmentValuesAction: assign(setInvestmentValuesAction),
      setSourceInvestmentIdAction: assign(setSourceInvestmentIdAction),
      addBookingValuesAction: assign(addBookingValuesAction),
      setBookingsAction: assign(setBookingsAction),
      updateSplitValuesAction: assign(updateSplitValuesAction),
      addValidationErrorAction: assign(addValidationErrorAction),
      clearValidationErrorAction: assign(clearValidationErrorAction),
      addAxiosErrorToContextAction: assign(addAxiosErrorToContextAction),
    },
  }
);
